/**
 * «Tarea ya realizada» (BHM-CLD-REQ-045b): la tarea sí procedía pero ya se ejecutó fuera de BarmetHub.
 * No es «Rechazar» y no suma nada a la jornada de hoy. La fecha es obligatoria y no puede ser futura.
 *
 * Uso (Mis tareas y recomendaciones de Nexus):
 *   {target && <AlreadyDoneDialog task={{ id, title, projectName, clientName }} source="MY_TASKS"
 *      onClose={() => setTarget(null)} onDone={(msg) => { notify(msg); void load(); }} />}
 * El diálogo hace la llamada, avisa a contador y flujo (`notifyActivityChanged`) y llama a `onDone(mensaje)`;
 * recargar la lista y mostrar el aviso es cosa de quien lo usa.
 */
import { useState } from 'react';
import { activityApi, errText } from '../../stores/activityApi';
import { todayMadrid } from '../../stores/workApi';
import { Modal, Field, Icon, ErrorBanner, inputCls, btnGhost, btnPrimary } from './WorkUi';
import { notifyActivityChanged } from './WorkdayCounter';
import { DONE_DURATION_ERROR, alreadyDoneHint, alreadyDoneToast, buildAlreadyDoneBody, isValidDoneDate, parseDoneDuration } from './flowLogic';

export interface AlreadyDoneTask { id: string; title: string; projectName?: string | null; clientName?: string | null }
export interface AlreadyDoneDialogProps {
  task: AlreadyDoneTask;
  /** De dónde viene la acción: la lista «Mis tareas» o una recomendación de Nexus. */
  source?: 'MY_TASKS' | 'NEXUS_RECOMMENDATION';
  onClose: () => void;
  /** Se llama tras guardar, con el aviso a mostrar («Tarea marcada como ya realizada. …»). */
  onDone?: (message: string) => void | Promise<void>;
}

export default function AlreadyDoneDialog({ task, source = 'MY_TASKS', onClose, onDone }: AlreadyDoneDialogProps) {
  const today = todayMadrid();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('');
  const [evidence, setEvidence] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const durationInvalid = parseDoneDuration(duration).state === 'invalid';
  const valid = isValidDoneDate(date, today) && !durationInvalid;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await activityApi.alreadyDone(task.id, buildAlreadyDoneBody({ date, time, duration, note, evidence }, source));
      notifyActivityChanged();
      onClose();
      await onDone?.(alreadyDoneToast(r?.effects));
    } catch (e) { setError(errText(e)); setBusy(false); }
  };

  return (
    <Modal title="Tarea ya realizada" onClose={onClose}>
      <div className="space-y-4" data-testid="already-done-dialog">
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <p className="text-sm text-on-surface"><b>{task.title}</b>{task.projectName && <span className="text-on-surface-variant"> · {task.projectName}</span>}</p>
        <p className="text-xs text-on-surface-variant">Usa esta opción cuando la tarea sí procedía pero ya se ejecutó. Si la tarea no procede, usa otra vía: esto no es rechazarla. No se suma nada a tu jornada de hoy.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="¿Cuándo se hizo? (obligatorio)">
            <input type="date" className={inputCls} value={date} max={today} onChange={(e) => setDate(e.target.value)} aria-label="Fecha en que se realizó" data-testid="ad-date" />
          </Field>
          <Field label="Hora (opcional)">
            <input type="time" className={inputCls} value={time} onChange={(e) => setTime(e.target.value)} aria-label="Hora en que se realizó" data-testid="ad-time" />
          </Field>
          <Field label="Tiempo dedicado en minutos (opcional)">
            <input type="number" min={1} max={1440} className={inputCls} value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="Si no lo sabes, déjalo vacío" aria-label="Tiempo dedicado en minutos" data-testid="ad-duration" />
          </Field>
          <Field label="Evidencia o referencia (opcional)">
            <input className={inputCls} maxLength={1000} value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="Enlace, correo, documento…" aria-label="Evidencia o referencia" data-testid="ad-evidence" />
          </Field>
        </div>
        <Field label="Aclaración (opcional)">
          <textarea className={`${inputCls} min-h-[72px]`} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Quién la hizo, dónde, por qué no constaba en BarmetHub…" aria-label="Aclaración" data-testid="ad-note" />
        </Field>
        {durationInvalid && <p className="text-xs text-[#F43F5E]">{DONE_DURATION_ERROR}</p>}
        <p className="text-xs text-outline">{alreadyDoneHint(duration)}</p>
        <div className="flex flex-wrap justify-end gap-2">
          <button className={btnGhost} onClick={onClose} data-testid="ad-cancel">Cancelar</button>
          <button className={btnPrimary} disabled={!valid || busy} onClick={submit} data-testid="ad-confirm"><Icon name="task_alt" className="text-base" />TAREA YA REALIZADA</button>
        </div>
      </div>
    </Modal>
  );
}
